import { useTranslation } from "@client-app/i18n";
import Skeleton from "@common/components/Skeleton";
import Tooltip from "@common/components/Tooltip";
import { WarningCircle } from "@phosphor-icons/react";
import BusinessGroupTooltipList, {
  businessGroupTooltipContentClassName,
} from "./BusinessGroupTooltipList";
import {
  getBusinessGroupDisplayName,
  type BrandRecord,
} from "./brandGroupViewModel";

export type MemberBusinessGroupsCellState = "loading" | "ready" | "error";

const MemberBusinessGroupsCell = ({
  businessGroups,
  state,
  onRetry,
}: {
  businessGroups: BrandRecord[];
  state: MemberBusinessGroupsCellState;
  onRetry?: () => void;
}) => {
  const { locale, t } = useTranslation();

  if (state === "loading") {
    return <Skeleton width="120px" height="20px" />;
  }

  if (state === "error") {
    const loadFailedLabel = t("website.brandsSettings.simple.loadFailed");
    return (
      <div className="flex min-w-0 items-center gap-2 text-sm text-[#B54708]">
        <WarningCircle size={16} className="shrink-0" aria-hidden="true" />
        <Tooltip content={loadFailedLabel} side="top" align="start">
          <span className="min-w-0 truncate">{loadFailedLabel}</span>
        </Tooltip>
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 font-medium text-[#1A162F] underline decoration-[#1A162F]/30 underline-offset-2 hover:decoration-[#1A162F]"
          aria-label={`${loadFailedLabel}. ${t("common.retry")}`}
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }

  if (businessGroups.length === 0) {
    return <span className="text-sm text-[#8A93B2]">—</span>;
  }

  const businessGroupNames = businessGroups.map(getBusinessGroupDisplayName);
  const businessGroupText = businessGroupNames.join(
    locale.toLowerCase().startsWith("zh") ? "、" : ", ",
  );

  return (
    <Tooltip
      content={
        <BusinessGroupTooltipList
          businessGroups={businessGroups.map((businessGroup) => ({
            id: businessGroup.id,
            name: getBusinessGroupDisplayName(businessGroup),
            logoUrl: businessGroup.logoUrl,
          }))}
          label={t("website.membersSettings.businessGroups")}
        />
      }
      contentClassName={businessGroupTooltipContentClassName}
      side="top"
      align="start"
    >
      <span
        tabIndex={0}
        aria-label={businessGroupText}
        className="block max-w-full truncate rounded-sm text-sm text-[#666F8D] outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
      >
        {businessGroupText}
      </span>
    </Tooltip>
  );
};

export default MemberBusinessGroupsCell;
